import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Pencil, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { StarRating } from "@/components/StarRating";
import { RatingText } from "@/components/UserIdentity";
import { FilePicker } from "@/components/FilePicker";
import { Avatar } from "@/components/Avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function UserProfile() {
  const { id } = useParams();
  const { t } = useLanguage();
  const { user: authUser, updateProfile, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [verif, setVerif] = useState(null);
  const [vForm, setVForm] = useState({ docType: "cc", docNumber: "", docPhoto: null });
  const [vSaving, setVSaving] = useState(false);
  const [vMsg, setVMsg] = useState(null);
  const vMsgRef = useRef(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/auth/export-data", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "boleia-connect-dados.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e, t));
    } finally {
      setExporting(false);
    }
  };
  const [photoFile, setPhotoFile] = useState(null);

  useEffect(() => {
    api.get(`/users/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [id]);

  useEffect(() => {
    if (authUser && authUser.id === id) {
      api.get("/my/verification").then((r) => setVerif(r.data)).catch(() => setVerif(false));
    }
  }, [authUser, id]);

  useEffect(() => {
    if (vMsg) vMsgRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [vMsg]);

  const deleteAccount = async () => {
    setDeleteSaving(true);
    setDeleteError("");
    try {
      await api.post("/auth/delete-account", { password: deletePassword });
      toast.success(t("profile.deleted"));
      navigate("/");
      try {
        await logout();
      } catch {
        // sessão já terminada no servidor
      }
    } catch (e) {
      setDeleteError(formatApiError(e, t));
    } finally {
      setDeleteSaving(false);
    }
  };

  if (data === null) return <div className="max-w-7xl mx-auto px-6 py-16 text-slate-500">{t("common.loading")}</div>;
  if (data === false) return <div className="max-w-7xl mx-auto px-6 py-16 text-slate-500">{t("common.error")}</div>;

  const { user, rating, reviews } = data;
  const tagEntries = Object.entries(rating.tags || {}).sort((a, b) => b[1] - a[1]);
  const isOwn = authUser && authUser.id === user.id;
  const tagLabel = (tag) => { const v = t(`tagLabels.${tag}`); return v === `tagLabels.${tag}` ? tag : v; };

  const submitVerification = async () => {
    setVSaving(true);
    setVMsg(null);
    try {
      const fd = new FormData();
      fd.append("document_type", vForm.docType);
      fd.append("document_number", vForm.docNumber);
      fd.append("document_photo", vForm.docPhoto);
      const { data: res } = await api.post("/verification", fd);
      if (res.status === "approved") {
        setVMsg({ type: "success", text: t("verify.approvedToast") });
        await refresh();
        const pr = await api.get(`/users/${id}`);
        setData(pr.data);
      } else {
        setVMsg({ type: "error", text: t("verify.mismatch") });
      }
      const r = await api.get("/my/verification");
      setVerif(r.data);
    } catch (e) {
      setVMsg({ type: "error", text: formatApiError(e, t) });
    } finally {
      setVSaving(false);
    }
  };

  const openEdit = () => {
    setForm({ name: authUser.name || "", email: authUser.email || "", phone: authUser.phone || "" });
    setSaveError("");
    setEditOpen(true);
  };

  const saveProfile = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await updateProfile({ name: form.name, email: form.email, phone: form.phone });
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post("/users/me/photo", fd);
        await refresh();
      }
      toast.success(t("common.profileSaved"));
      setEditOpen(false);
      const r = await api.get(`/users/${id}`);
      setData(r.data);
    } catch (e) {
      setSaveError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="user-profile-page">
      <div className="bg-white rounded-2xl border border-slate-100 p-8 md:p-10 shadow-sm mb-8">
        <div className="flex items-center gap-5">
          <Avatar user={user} size="w-16 h-16" text="text-2xl" testid="profile-avatar" />
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-primary" data-testid="profile-name">{user.name}</h1>
              {user.verified && <span data-testid="verified-badge" className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full"><ShieldCheck size={13} />{t("verify.badge")}</span>}
              {isOwn && <Button size="sm" variant="outline" data-testid="edit-profile-button" onClick={openEdit} className="rounded-full"><Pencil size={13} className="mr-1.5" />{t("common.editProfile")}</Button>}
            </div>
            <p className="text-sm text-slate-400">{t("profile.memberSince")} {user.created_at?.slice(0, 10)}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <RatingText rating={rating} starSize={16} textClass="text-sm font-semibold text-primary" testid="profile-rating" />
            </div>
          </div>
        </div>
        {tagEntries.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6" data-testid="profile-tags">
            {tagEntries.map(([tag, count]) => (
              <span key={tag} className="bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full">{tagLabel(tag)} · {count}</span>
            ))}
          </div>
        )}
      </div>

      {isOwn && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8 shadow-sm mb-8" data-testid="verification-section">
          <h2 className="text-lg font-bold text-primary mb-1">{t("verify.title")}</h2>
          <p className="text-xs text-slate-400 mb-4">{t("verify.optional")} {t("verify.note")}</p>
          {verif === null ? (
            <p className="text-sm text-slate-400">{t("common.loading")}</p>
          ) : verif && verif.verified ? (
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-xl p-4" data-testid="verification-approved">
              <ShieldCheck size={18} /> <span className="text-sm font-medium">{t("verify.approvedText")}</span>
            </div>
          ) : verif && verif.verification?.status === "pending" ? (
            <div className="bg-amber-50 text-amber-800 rounded-xl p-4 text-sm" data-testid="verification-pending">{t("verify.pendingText")}</div>
          ) : (
            <div className="space-y-4">
              {verif && verif.verification?.status === "rejected" && <p className="text-sm text-destructive" data-testid="verification-rejected">{t("verify.rejectedText")}</p>}
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>{t("verify.docType")}</Label>
                  <Select value={vForm.docType} onValueChange={(v) => setVForm({ ...vForm, docType: v })}>
                    <SelectTrigger data-testid="verify-doc-type" className="mt-1.5 h-12 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cc" data-testid="doc-cc">{t("verify.cc")}</SelectItem>
                      <SelectItem value="passport" data-testid="doc-passport">{t("verify.passport")}</SelectItem>
                      <SelectItem value="driving_license" data-testid="doc-driving">{t("verify.driving")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("verify.docNumber")}</Label>
                  <Input data-testid="verify-doc-number" value={vForm.docNumber} onChange={(e) => setVForm({ ...vForm, docNumber: e.target.value })} className="mt-1.5 h-12 rounded-xl" />
                </div>
              </div>
              <div>
                <Label>{t("verify.docPhoto")}</Label>
                <FilePicker testid="verify-doc-photo" accept="image/*,application/pdf" file={vForm.docPhoto} onChange={(f) => setVForm({ ...vForm, docPhoto: f })} />
                <p className="text-xs text-slate-400 mt-1.5">{t("verify.docPhotoNote")}</p>
              </div>
              {vMsg && (
                <div ref={vMsgRef} data-testid="verify-message" className={`rounded-xl p-4 text-sm font-medium ${vMsg.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-destructive"}`}>{vMsg.text}</div>
              )}
              <Button data-testid="verify-submit" onClick={submitVerification} disabled={vSaving || vForm.docNumber.trim().length < 4 || !vForm.docPhoto} className="rounded-full bg-primary hover:bg-primary/90 text-white">{t("verify.submit")}</Button>
            </div>
          )}
        </div>
      )}

      <h2 className="text-xl font-bold text-primary mb-5">{t("profile.reviewsTitle")}</h2>
      {reviews.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="profile-no-reviews">{t("profile.noReviews")}</p>
      ) : (
        <div className="space-y-4" data-testid="profile-reviews">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <Avatar user={{ name: r.reviewer_name, photo_url: r.reviewer_photo }} size="w-8 h-8" text="text-xs" testid={`review-avatar-${r.id}`} />
                  <p className="font-semibold text-sm text-primary">{r.reviewer_name}</p>
                  {r.reviewee_role && <Badge variant="secondary" className="text-[10px]" data-testid={`review-role-${r.id}`}>{r.reviewee_role === "driver" ? t("review.asDriver") : r.reviewee_role === "passenger" ? t("review.asPassenger") : t("review.asSender")}</Badge>}
                </div>
                <StarRating value={r.rating} size={13} />
              </div>
              {r.comment && <p className="text-sm text-slate-600 mb-2">{r.comment}</p>}
              {r.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {r.tags.map((tag) => <span key={tag} className="bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full">{tagLabel(tag)}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {isOwn && (
        <div className="border border-slate-200 rounded-2xl p-6 bg-white shadow-sm mb-4" data-testid="export-data-section">
          <h2 className="font-heading text-lg font-bold text-primary mb-1">{t("profile.exportTitle")}</h2>
          <p className="text-sm text-slate-600 mb-4">{t("profile.exportText")}</p>
          <Button variant="outline" data-testid="export-data-button" onClick={exportData} disabled={exporting} className="rounded-full">{t("profile.exportBtn")}</Button>
        </div>
      )}
      {isOwn && (
        <div className="border border-red-200 rounded-2xl p-6 bg-red-50/40 mb-8" data-testid="delete-account-section">
          <h2 className="font-heading text-lg font-bold text-destructive mb-1">{t("profile.deleteTitle")}</h2>
          <p className="text-sm text-slate-600 mb-4">{t("profile.deleteText")}</p>
          <Button variant="outline" data-testid="delete-account-button" onClick={() => { setDeleteOpen(true); setDeletePassword(""); setDeleteError(""); }} className="rounded-full border-destructive text-destructive hover:bg-red-50">{t("profile.deleteBtn")}</Button>
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl" data-testid="edit-profile-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{t("common.editProfile")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="ep-name">{t("auth.name")}</Label>
              <Input id="ep-name" data-testid="edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="ep-email">{t("auth.email")}</Label>
              <Input id="ep-email" data-testid="edit-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-12 rounded-xl" />
            </div>
            <div>
              <Label>{t("photo.change")}</Label>
              <FilePicker testid="edit-photo-input" file={photoFile} onChange={setPhotoFile} />
            </div>
            <div>
              <Label htmlFor="ep-phone">{t("common.phone")}</Label>
              <Input id="ep-phone" data-testid="edit-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+351 912 345 678" className="mt-1.5 h-12 rounded-xl" />
              <p className="text-xs text-slate-400 mt-1.5">{t("common.phoneHint")}</p>
            </div>
            {saveError && <p className="text-sm text-destructive" data-testid="edit-profile-error">{saveError}</p>}
            <Button data-testid="edit-profile-save" onClick={saveProfile} disabled={saving} className="w-full h-12 rounded-full bg-accent hover:bg-accent/90 text-white font-semibold">{t("common.save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="rounded-2xl" data-testid="delete-account-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{t("profile.deleteConfirmTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">{t("profile.deleteConfirmText")}</p>
            <div>
              <Label htmlFor="delete-password">{t("auth.password")}</Label>
              <PasswordInput id="delete-password" data-testid="delete-password-input" value={deletePassword} onChange={(e) => { setDeletePassword(e.target.value); setDeleteError(""); }} className="mt-1.5 h-12 rounded-xl" />
            </div>
            {deleteError && <p className="text-sm text-destructive" data-testid="delete-account-error">{deleteError}</p>}
            <div className="flex gap-3 justify-end">
              <Button variant="outline" data-testid="delete-account-cancel" onClick={() => setDeleteOpen(false)} className="rounded-full">{t("dashboard.confirmNo")}</Button>
              <Button data-testid="delete-account-confirm" onClick={deleteAccount} disabled={deleteSaving || !deletePassword} className="rounded-full bg-destructive hover:bg-destructive/90 text-white">{t("profile.deleteConfirmBtn")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
